import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowUpRight, Search } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSearchResults } from "../data/siteSearch";

const RESULT_GROUPS = ["Works", "Exhibitions", "Prints", "Pages"];

function groupSlug(group) {
  return group.toLowerCase().replace(/\s+/g, "-");
}

export function SearchResultsPage({ searchItems }) {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search).get("q")?.trim() || "";
  const [input, setInput] = useState(query);
  const results = useMemo(() => getSearchResults(searchItems, query), [searchItems, query]);
  const groupedResults = RESULT_GROUPS.map((group) => ({
    group,
    items: results.filter(({ item }) => item.group === group),
  })).filter(({ items }) => items.length > 0);

  useEffect(() => {
    setInput(query);
  }, [query]);

  const submitSearch = (event) => {
    event.preventDefault();
    const nextQuery = input.trim();
    navigate(nextQuery ? `/search?q=${encodeURIComponent(nextQuery)}` : "/search");
  };

  return (
    <section className="search-results-page" data-testid="search-results-page">
      <div className="section-shell">
        <Link className="search-results-back" to="/" data-testid="search-results-back-link"><ArrowLeft size={15} strokeWidth={1.3} /> Return to the studio</Link>
        <div className="search-results-heading" data-testid="search-results-heading">
          <p className="section-kicker">{query ? `Results for “${query}”` : "Begin with a word"}</p>
          <h1>Find a way<br /><em>through the work.</em></h1>
        </div>
        <form className="search-results-form" onSubmit={submitSearch} data-testid="search-results-form">
          <label className="sr-only" htmlFor="search-results-input">Search the public site</label>
          <Search size={17} strokeWidth={1.4} aria-hidden="true" />
          <input id="search-results-input" type="search" value={input} onChange={(event) => setInput(event.target.value)} placeholder="Title, series, medium, venue, or page" autoComplete="off" data-testid="search-results-input" />
          <button className="button button-primary" type="submit" data-testid="search-results-submit">Browse</button>
        </form>
        {query && results.length > 0 && <p className="search-results-count" data-testid="search-results-count">{results.length} {results.length === 1 ? "option" : "options"} found</p>}
        {!query && (
          <div className="search-results-empty" data-testid="search-results-empty">
            <p>Type a name, place, series, medium, or page above.</p>
          </div>
        )}
        {query && !results.length && (
          <div className="search-results-empty" data-testid="search-results-no-match">
            <p>No public match for “{query}”.</p>
            <Link className="button button-text" to="/search" data-testid="search-results-clear">Clear search <ArrowUpRight size={15} strokeWidth={1.3} /></Link>
          </div>
        )}
        {groupedResults.length > 0 && (
          <div className="search-results-groups" data-testid="search-results-groups">
            {groupedResults.map(({ group, items }) => (
              <section className="search-results-group" key={group} data-testid={`search-results-group-${groupSlug(group)}`}>
                <div className="search-results-group-heading">
                  <p className="section-kicker">{group}</p>
                  <span>{String(items.length).padStart(2, "0")}</span>
                </div>
                <div className="search-results-list">
                  {items.map(({ item }, index) => (
                    <Link className="search-result-link" key={`${item.type}-${item.label}-${index}`} to={item.path} data-testid={`search-result-${groupSlug(group)}-${index}`}>
                      <span>
                        <strong>{item.label}</strong>
                        <small>{item.detail}</small>
                      </span>
                      <ArrowUpRight size={16} strokeWidth={1.3} />
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
